/** Lightweight markdown → HTML for protocol display (no extra deps). */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function inlineFormat(text: string): string {
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
}

function parseTable(lines: string[], start: number): { html: string; next: number } {
  const rows: string[][] = [];
  let i = start;
  while (i < lines.length && lines[i].includes('|')) {
    const row = lines[i]
      .split('|')
      .map(c => c.trim())
      .filter((_, idx, arr) => (idx > 0 && idx < arr.length - 1) || (arr.length === 1 && !!arr[0]));
    if (row.length && !row.every(c => /^:?-+:?$/.test(c))) {
      rows.push(row);
    }
    i++;
  }
  if (!rows.length) return { html: '', next: start };

  const [head, ...body] = rows;
  const thead = `<thead><tr>${head.map(c => `<th>${inlineFormat(c)}</th>`).join('')}</tr></thead>`;
  const tbody = `<tbody>${body.map(r =>
    `<tr>${r.map(c => `<td>${inlineFormat(c)}</td>`).join('')}</tr>`,
  ).join('')}</tbody>`;
  return { html: `<table class="md-table">${thead}${tbody}</table>`, next: i };
}

export function markdownToHtml(markdown: string): string {
  if (!markdown?.trim()) return '';

  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i++;
      continue;
    }

    if (trimmed.startsWith('|')) {
      const { html, next } = parseTable(lines, i);
      if (html) {
        out.push(html);
        i = next;
        continue;
      }
    }

    if (/^---+$/.test(trimmed)) {
      out.push('<hr />');
      i++;
      continue;
    }

    const h3 = trimmed.match(/^###\s+(.+)/);
    if (h3) {
      out.push(`<h3>${inlineFormat(h3[1])}</h3>`);
      i++;
      continue;
    }

    const h2 = trimmed.match(/^##\s+(.+)/);
    if (h2) {
      out.push(`<h2>${inlineFormat(h2[1])}</h2>`);
      i++;
      continue;
    }

    const h1 = trimmed.match(/^#\s+(.+)/);
    if (h1) {
      out.push(`<h1>${inlineFormat(h1[1])}</h1>`);
      i++;
      continue;
    }

    if (/^[-*•]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*•]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*•]\s+/, ''));
        i++;
      }
      out.push(`<ul>${items.map(it => `<li>${inlineFormat(it)}</li>`).join('')}</ul>`);
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length) {
        const cur = lines[i].trim();
        if (/^\d+\.\s+/.test(cur)) {
          items.push(cur.replace(/^\d+\.\s+/, ''));
          i++;
          while (i < lines.length && lines[i].trim() && !/^\d+\.\s+/.test(lines[i].trim()) && !/^#{1,3}\s/.test(lines[i].trim()) && !lines[i].trim().startsWith('|')) {
            items[items.length - 1] += ' ' + lines[i].trim();
            i++;
          }
        } else {
          break;
        }
      }
      out.push(`<ol>${items.map(it => `<li>${inlineFormat(it)}</li>`).join('')}</ol>`);
      continue;
    }

    const para: string[] = [trimmed];
    i++;
    while (i < lines.length && lines[i].trim() && !/^#{1,3}\s/.test(lines[i].trim()) && !lines[i].trim().startsWith('|') && !/^[-*•]\s+/.test(lines[i].trim()) && !/^\d+\.\s+/.test(lines[i].trim())) {
      para.push(lines[i].trim());
      i++;
    }
    out.push(`<p>${inlineFormat(para.join(' '))}</p>`);
  }

  return out.join('\n');
}
